import { CURRENCY_MICROS_PER_UNIT, type CurrencyValue } from '@ofmos/shared/field-types';

/**
 * Value formatting for display. One locale constant for the whole app: UI copy is
 * English first and DE i18n arrives later at the UI layer only (INV-16), so a
 * hard-coded locale here is a decision, not an oversight - and it keeps rendered
 * output deterministic in tests.
 */
export const DISPLAY_LOCALE = 'en-US';

export function formatNumber(value: number): string {
  return new Intl.NumberFormat(DISPLAY_LOCALE, { maximumFractionDigits: 2 }).format(value);
}

/** `2026-07-26` -> `Jul 26, 2026`. Date-only values never get a timezone applied. */
export function formatDate(iso: string): string {
  const [year, month, day] = iso.split('-').map((part) => Number.parseInt(part, 10));
  if (year === undefined || month === undefined || day === undefined) {
    return iso;
  }
  return new Intl.DateTimeFormat(DISPLAY_LOCALE, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(year, month - 1, day)));
}

/**
 * Timestamps render in UTC. A CRM whose users sit in one timezone still gets
 * audited from another, and "16:04" without a frame of reference is the kind of
 * detail that costs an hour in a support call.
 */
export function formatDateTime(iso: string, locale = DISPLAY_LOCALE): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return iso;
  }
  return `${new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
    hour12: false,
  }).format(date)} UTC`;
}

/** Relative time for timelines ("3 days ago"), absolute stays available on hover. */
export function formatRelative(iso: string, now: Date = new Date()): string {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) {
    return iso;
  }
  const seconds = Math.round((then.getTime() - now.getTime()) / 1000);
  const units: readonly [Intl.RelativeTimeFormatUnit, number][] = [
    ['year', 31_536_000],
    ['month', 2_592_000],
    ['day', 86_400],
    ['hour', 3600],
    ['minute', 60],
  ];
  const formatter = new Intl.RelativeTimeFormat(DISPLAY_LOCALE, { numeric: 'auto' });
  for (const [unit, size] of units) {
    if (Math.abs(seconds) >= size) {
      return formatter.format(Math.round(seconds / size), unit);
    }
  }
  /**
   * UNDER A MINUTE IT IS THE SECONDS UNIT AT ZERO, which this locale renders "now" (wave 4
   * round 2). The minute unit at zero renders "this minute" - Intl's own words for the
   * CURRENT minute, and what a just-written note read in the record's stream. Apple's lists
   * say "now" (Mail) and "Just Now" (Messages) for the same moment, and the copy deck's R7
   * takes relative words only as the formatter gives them, so the fix is the unit and not a
   * hand-written string.
   * doc: https://developer.apple.com/documentation/foundation/relativedatetimeformatter
   */
  return formatter.format(0, 'second');
}

/** Micros are exact integers; the display divides only at the very end. */
export function formatCurrency(value: CurrencyValue): string {
  const amount = value.amountMicros / CURRENCY_MICROS_PER_UNIT;
  try {
    return new Intl.NumberFormat(DISPLAY_LOCALE, {
      style: 'currency',
      currency: value.currencyCode,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    // An unknown ISO code must not blank the cell.
    return `${formatNumber(amount)} ${value.currencyCode}`;
  }
}

export function currencyUnits(value: CurrencyValue): number {
  return value.amountMicros / CURRENCY_MICROS_PER_UNIT;
}

export function unitsToMicros(units: number): number {
  return Math.round(units * CURRENCY_MICROS_PER_UNIT);
}

/** `https://vault.example/mila` -> `vault.example/mila` (scheme is noise in a cell). */
export function formatUrl(url: string): string {
  return url.replace(/^https?:\/\//, '').replace(/\/$/, '');
}
